/**
 * Public Site Content — admin UI for content that appears on the
 * weavein.ai marketing site.
 *
 * Two tabs:
 *   - Blog Articles (drafts → published on weavein.ai/blog)
 *   - WeaveIn.ai Resources (articles / videos / guides / weblinks shown
 *     on the public resources index)
 *
 * Media Library (the asset library used by campaigns) used to live here
 * as a third tab. It was moved to Communications Hub → Setup → Media
 * Library to keep this surface focused on public-site content for web
 * managers. See migration 366.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Globe,
  ArrowLeft,
  Loader2,
  X,
  ExternalLink,
  BookOpen,
  PenTool,
  CheckCircle,
  Send
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';

// Types
interface BlogArticle {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  featured_image_url?: string;
  status: 'draft' | 'published' | 'archived';
  published_at?: string;
  author_name?: string;
  tags: string[];
  category?: string;
  created_at: string;
  updated_at: string;
}

interface ResourcePost {
  id: number;
  title: string;
  description?: string;
  resource_type: 'article' | 'video' | 'document' | 'webinar' | 'guide' | 'weblink';
  source_id?: number;
  external_url?: string;
  thumbnail_url?: string;
  is_featured: boolean;
  is_published: boolean;
  display_order: number;
  created_at: string;
}

type ActiveTab = 'articles' | 'resources';
type ModalMode = 'article' | 'resource' | 'edit-article' | 'edit-resource' | null;

export default function ResourcesManagementPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('articles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [saving, setSaving] = useState(false);

  // Blog Articles State
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<BlogArticle | null>(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image_url: '',
    status: 'draft' as 'draft' | 'published',
    tags: [] as string[],
    category: ''
  });

  // Resources State
  const [resources, setResources] = useState<ResourcePost[]>([]);
  const [selectedResource, setSelectedResource] = useState<ResourcePost | null>(null);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    resource_type: 'article' as ResourcePost['resource_type'],
    external_url: '',
    thumbnail_url: '',
    is_featured: false,
    is_published: false
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '';
    return { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId };
  }, []);

  useEffect(() => {
    if (activeTab === 'articles') {
      loadArticles();
    } else if (activeTab === 'resources') {
      loadResources();
    }
  }, [activeTab]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${getWeaveinApiUrl()}/api/resources/articles`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
      } else if (res.status === 404) {
        // API endpoint may not exist yet - use empty array
        setArticles([]);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to load articles:', err);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadResources = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${getWeaveinApiUrl()}/api/resources/posts`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setResources(data.posts || []);
      } else if (res.status === 404) {
        setResources([]);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to load resources:', err);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  // Save blog article
  const handleArticleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const isEdit = modalMode === 'edit-article' && selectedArticle;

      const url = isEdit
        ? `${getWeaveinApiUrl()}/api/resources/articles/${selectedArticle.id}`
        : `${getWeaveinApiUrl()}/api/resources/articles`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...articleForm,
          slug: articleForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
        })
      });

      if (!res.ok) throw new Error('Failed to save article');

      setModalMode(null);
      resetArticleForm();
      loadArticles();
    } catch (err: any) {
      console.error('Article save error:', err);
      setError(err.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  // Save resource post
  const handleResourceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const isEdit = modalMode === 'edit-resource' && selectedResource;

      const url = isEdit
        ? `${getWeaveinApiUrl()}/api/resources/posts/${selectedResource.id}`
        : `${getWeaveinApiUrl()}/api/resources/posts`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(resourceForm)
      });

      if (!res.ok) throw new Error('Failed to save resource');

      setModalMode(null);
      resetResourceForm();
      loadResources();
    } catch (err: any) {
      console.error('Resource save error:', err);
      setError(err.message || 'Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (articleId: number) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/resources/articles/${articleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        loadArticles();
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete article');
    }
  };

  const handleDeleteResource = async (resourceId: number) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/resources/posts/${resourceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        loadResources();
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete resource');
    }
  };

  // Publish to WeaveIn.ai
  const handlePublishToWeaveIn = async (resourceId: number) => {
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/resources/posts/${resourceId}/publish`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        loadResources();
      }
    } catch (err) {
      console.error('Publish error:', err);
      setError('Failed to publish resource');
    }
  };

  // Reset forms
  const resetArticleForm = () => {
    setSelectedArticle(null);
    setArticleForm({
      title: '',
      content: '',
      excerpt: '',
      featured_image_url: '',
      status: 'draft',
      tags: [],
      category: ''
    });
  };

  const resetResourceForm = () => {
    setSelectedResource(null);
    setResourceForm({
      title: '',
      description: '',
      resource_type: 'article',
      external_url: '',
      thumbnail_url: '',
      is_featured: false,
      is_published: false
    });
  };

  // Open edit modals
  const openEditArticle = (article: BlogArticle) => {
    setSelectedArticle(article);
    setArticleForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || '',
      featured_image_url: article.featured_image_url || '',
      status: article.status === 'archived' ? 'draft' : article.status,
      tags: article.tags || [],
      category: article.category || ''
    });
    setModalMode('edit-article');
  };

  const openEditResource = (resource: ResourcePost) => {
    setSelectedResource(resource);
    setResourceForm({
      title: resource.title,
      description: resource.description || '',
      resource_type: resource.resource_type,
      external_url: resource.external_url || '',
      thumbnail_url: resource.thumbnail_url || '',
      is_featured: resource.is_featured,
      is_published: resource.is_published
    });
    setModalMode('edit-resource');
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Content"
        title="Public Site Content"
        description="Manage blog articles and WeaveIn.ai resources for the public marketing site. Need to manage campaign assets? Use Communications Hub → Setup → Media Library."
        actions={
          <>
            <button
              onClick={() => {
                localStorage.setItem('activeTab', 'dashboard');
                window.dispatchEvent(new Event('storage'));
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            {activeTab === 'articles' && (
              <button
                onClick={() => {
                  resetArticleForm();
                  setModalMode('article');
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
              >
                <PenTool className="w-4 h-4" />
                Write Article
              </button>
            )}
            {activeTab === 'resources' && (
              <button
                onClick={() => {
                  resetResourceForm();
                  setModalMode('resource');
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
              >
                <Plus className="w-4 h-4" />
                Add Resource
              </button>
            )}
          </>
        }
        tabs={[
          { key: 'articles', label: 'Blog Articles' },
          { key: 'resources', label: 'WeaveIn.ai Resources' },
        ]}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as ActiveTab)}
      />
      <PageContainer>

      {/* Error Message */}
      {error && (
        <div>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        {activeTab === 'articles' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#5B4B8A]" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12 bg-surface rounded-xl">
                <PenTool className="w-12 h-12 text-ink-400 mx-auto mb-4" />
                <p className="text-ink-500">No blog articles yet</p>
                <button
                  onClick={() => {
                    resetArticleForm();
                    setModalMode('article');
                  }}
                  className="mt-4 text-[#5CBDB9] hover:text-[#4A9E9B] font-medium"
                >
                  Write your first article
                </button>
              </div>
            ) : (
              <div className="bg-surface rounded-xl shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-paper dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-v2border-muted">
                    {articles.map((article) => (
                      <tr key={article.id} className="hover:bg-paper-2/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-ink">{article.title}</p>
                            {article.excerpt && (
                              <p className="text-sm text-ink-500 truncate max-w-md">{article.excerpt}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            article.status === 'published'
                              ? 'bg-green-100 text-green-700'
                              : article.status === 'archived'
                              ? 'bg-paper-2 text-ink-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {article.status === 'published' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            {article.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-500">{article.category || '-'}</td>
                        <td className="px-6 py-4 text-sm text-ink-500">
                          {new Date(article.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditArticle(article)}
                              className="p-2 text-ink-400 hover:text-[#5B4B8A]"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteArticle(article.id)}
                              className="p-2 text-ink-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'resources' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#5B4B8A]" />
              </div>
            ) : resources.length === 0 ? (
              <div className="text-center py-12 bg-surface rounded-xl">
                <Globe className="w-12 h-12 text-ink-400 mx-auto mb-4" />
                <p className="text-ink-500">No resources posted to WeaveIn.ai yet</p>
                <button
                  onClick={() => {
                    resetResourceForm();
                    setModalMode('resource');
                  }}
                  className="mt-4 text-[#5CBDB9] hover:text-[#4A9E9B] font-medium"
                >
                  Add your first resource
                </button>
              </div>
            ) : (
              <div className="bg-surface rounded-xl shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-paper dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Resource</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Featured</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-v2border-muted">
                    {resources.map((resource) => (
                      <tr key={resource.id} className="hover:bg-paper-2/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-ink">{resource.title}</p>
                            {resource.description && (
                              <p className="text-sm text-ink-500 truncate max-w-md">{resource.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-paper-2 text-ink-700 capitalize">
                            {resource.resource_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            resource.is_published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {resource.is_published ? <CheckCircle className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            {resource.is_published ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {resource.is_featured && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              Featured
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!resource.is_published && (
                              <button
                                onClick={() => handlePublishToWeaveIn(resource.id)}
                                className="p-2 text-ink-400 hover:text-green-500"
                                title="Publish to WeaveIn.ai"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openEditResource(resource)}
                              className="p-2 text-ink-400 hover:text-[#5B4B8A]"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteResource(resource.id)}
                              className="p-2 text-ink-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Article Modal */}
      {(modalMode === 'article' || modalMode === 'edit-article') && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-v2border">
              <h2 className="text-xl font-bold text-ink">
                {modalMode === 'edit-article' ? 'Edit Article' : 'Write New Article'}
              </h2>
            </div>
            <form onSubmit={handleArticleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={articleForm.title}
                  onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                  placeholder="Article title"
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={12}
                  required
                  value={articleForm.content}
                  onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                  placeholder="Write your article content here... (Markdown supported)"
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink font-mono text-sm"
                />
                <p className="text-xs text-ink-500 mt-1">Markdown formatting is supported</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Excerpt
                </label>
                <textarea
                  rows={2}
                  value={articleForm.excerpt}
                  onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                  placeholder="Brief summary for previews..."
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={articleForm.category}
                    onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                    placeholder="e.g., SDOH, Benefits, Case Study"
                    className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Status
                  </label>
                  <select
                    value={articleForm.status}
                    onChange={(e) => setArticleForm({ ...articleForm, status: e.target.value as 'draft' | 'published' })}
                    className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Featured Image URL
                </label>
                <input
                  type="url"
                  value={articleForm.featured_image_url}
                  onChange={(e) => setArticleForm({ ...articleForm, featured_image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-v2border">
                <button
                  type="button"
                  onClick={() => {
                    setModalMode(null);
                    resetArticleForm();
                  }}
                  className="px-4 py-2 text-ink-700 hover:bg-paper-2 dark:hover:bg-gray-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-[#5CBDB9] text-white rounded-lg hover:bg-[#4A9E9B] disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modalMode === 'edit-article' ? 'Save Changes' : 'Create Article'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resource Modal */}
      {(modalMode === 'resource' || modalMode === 'edit-resource') && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-v2border">
              <h2 className="text-xl font-bold text-ink">
                {modalMode === 'edit-resource' ? 'Edit Resource' : 'Add Resource to WeaveIn.ai'}
              </h2>
            </div>
            <form onSubmit={handleResourceSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={resourceForm.title}
                  onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                  placeholder="Resource title"
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Resource Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={resourceForm.resource_type}
                  onChange={(e) => setResourceForm({ ...resourceForm, resource_type: e.target.value as ResourcePost['resource_type'] })}
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                >
                  <option value="article">Article</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="webinar">Webinar</option>
                  <option value="guide">Guide</option>
                  <option value="weblink">Web Link</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={resourceForm.description}
                  onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                  placeholder="Brief description..."
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  External URL
                </label>
                <input
                  type="url"
                  value={resourceForm.external_url}
                  onChange={(e) => setResourceForm({ ...resourceForm, external_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Thumbnail URL
                </label>
                <input
                  type="url"
                  value={resourceForm.thumbnail_url}
                  onChange={(e) => setResourceForm({ ...resourceForm, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={resourceForm.is_featured}
                    onChange={(e) => setResourceForm({ ...resourceForm, is_featured: e.target.checked })}
                    className="w-4 h-4 text-[#5CBDB9] border-v2border rounded focus:ring-[#5CBDB9]"
                  />
                  <span className="text-sm text-ink-700">Featured resource</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={resourceForm.is_published}
                    onChange={(e) => setResourceForm({ ...resourceForm, is_published: e.target.checked })}
                    className="w-4 h-4 text-[#5CBDB9] border-v2border rounded focus:ring-[#5CBDB9]"
                  />
                  <span className="text-sm text-ink-700">Publish immediately</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-v2border">
                <button
                  type="button"
                  onClick={() => {
                    setModalMode(null);
                    resetResourceForm();
                  }}
                  className="px-4 py-2 text-ink-700 hover:bg-paper-2 dark:hover:bg-gray-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-[#5CBDB9] text-white rounded-lg hover:bg-[#4A9E9B] disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modalMode === 'edit-resource' ? 'Save Changes' : 'Add Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </PageContainer>
    </div>
  );
}
